import { useState } from "react";
import { toast } from "sonner";
import { Eye, Lock, CalendarClock, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { VISIBILITY, visibilityLabel } from "@/lib/studio";

const pad = (n) => String(n).padStart(2, "0");

const SchedulePicker = ({ initial, onSet }) => {
  const start = initial ? new Date(initial) : null;
  const [day, setDay] = useState(start);
  const [time, setTime] = useState(start ? `${pad(start.getHours())}:${pad(start.getMinutes())}` : "09:00");
  const [open, setOpen] = useState(false);
  const when = day && (() => { const d = new Date(day); const [h, m] = time.split(":").map(Number); d.setHours(h || 0, m || 0, 0, 0); return d; })();
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 w-44 justify-start font-normal" data-testid="visibility-date"><CalendarDays className="mr-2 h-4 w-4" />{day ? day.toLocaleDateString() : "Pick a date"}</Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start"><Calendar mode="single" selected={day} onSelect={(d) => { setDay(d); setOpen(false); }} initialFocus data-testid="visibility-calendar" /></PopoverContent>
      </Popover>
      <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-9 w-28 [color-scheme:dark]" data-testid="visibility-time" />
      <Button size="sm" variant="outline" disabled={!when} onClick={() => onSet(when)} data-testid="visibility-schedule">Set</Button>
    </>
  );
};

export const VisibilityControl = ({ story, onSaved }) => {
  const [mode, setMode] = useState(story.visibility);
  const save = async (visibility, at) => {
    try {
      await api.patch(`/stories/${story.id}`, { visibility, ...(visibility === "scheduled" ? { publish_at: at.toISOString() } : {}) });
      toast.success(visibility === "private" ? "Story is private" : visibility === "published" ? "Story is visible to editors & viewers" : "Visibility scheduled");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const pick = (v) => { setMode(v); if (v !== "scheduled") save(v); };
  const Icon = mode === "private" ? Lock : mode === "scheduled" ? CalendarClock : Eye;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="visibility-control">
      <span className="lf-label mr-1">Team access</span>
      <Icon className="h-4 w-4 text-[#A8A090]" />
      <Select value={mode} onValueChange={pick}>
        <SelectTrigger className="h-9 w-60" data-testid="visibility-select"><SelectValue /></SelectTrigger>
        <SelectContent>{VISIBILITY.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`visibility-${k}`}>{l}</SelectItem>)}</SelectContent>
      </Select>
      {mode === "scheduled" && <SchedulePicker initial={story.publish_at} onSet={(d) => save("scheduled", d)} />}
      <span className="text-xs text-[#A8A090]" data-testid="visibility-status">{mode === "scheduled" && story.visibility !== "scheduled" ? "Pick a date and time, then press Set" : visibilityLabel(story)}</span>
    </div>
  );
};
